<template>
  <div class="cover-container w-100 mx-auto" id="main_page" style="overflow: hidden;">
    <!-- WebAR.rocks.hand 自己管理相機/偵測，畫在這個 canvas 上（含影像本身） -->
    <canvas ref="handTrackerCanvas" class="tracker-canvas"></canvas>
    <!-- Three.js 疊加層（透明背景），手環模型畫在這一層 -->
    <canvas ref="vtoCanvas" class="vto-canvas"></canvas>

    <!-- 載入中 -->
    <div class="loading-overlay" v-if="!isReady && !errorMessage">
      <div class="loading-spinner"></div>
      <p>{{ languageTexts[currentLanguage].loadingText }}</p>
    </div>

    <!-- 錯誤訊息（相機權限被拒 / 模型載入失敗等） -->
    <div class="error-overlay" v-if="errorMessage">
      <p>{{ errorMessage }}</p>
    </div>

    <!-- 尚未偵測到手腕的提示 -->
    <div class="wrist-hint" v-if="isReady && !errorMessage && !handDetected">
      <div class="wrist-hint-text">{{ languageTexts[currentLanguage].wristHint }}</div>
    </div>

    <!-- 手腕擺放教學：照抄官方 demos/VTO 範例本來就有的引導圖，第一次進來先顯示 -->
    <div class="instructions-overlay" v-if="showInstructions">
      <div class="instructions-box">
        <p class="instructions-text">{{ languageTexts[currentLanguage].instructionsText }}</p>
        <img :src="wristGuidelineSrc" class="instructions-img" alt="wrist guideline" />
        <button class="instructions-ok-btn" @click="showInstructions = false">{{ languageTexts[currentLanguage].instructionsOk }}</button>
      </div>
    </div>
    <div class="instructions-reopen" v-if="isReady && !errorMessage && !showInstructions" @click="showInstructions = true">?</div>

    <!-- 開發者模式：對照官方 demo 的 .controls，切換 wrist／wristBack 兩種偵測模式比較效果，
         另外加一組平滑係數調整（越小越平滑但延遲越高） -->
    <div class="dev-controls" v-if="devMode">
      <button class="dev-mode-btn" :class="{ active: activeModel === 'wristDemo' }" @click="loadModel('wristDemo')">手掌朝鏡頭</button>
      <button class="dev-mode-btn" :class="{ active: activeModel === 'wristBackDemo' }" @click="loadModel('wristBackDemo')">手背朝鏡頭</button>
      <button class="dev-mode-btn" @click="devAdjustSmoothing(-0.05)">平滑 −</button>
      <span class="dev-mode-btn dev-smoothing-value">{{ devSmoothingAlpha.toFixed(2) }}</span>
      <button class="dev-mode-btn" @click="devAdjustSmoothing(0.05)">平滑 +</button>
    </div>

    <TopNavigation :showIndexBtn="true" :showMapBtn="true" :showDataBtn="false"
      :currentLanguage="currentLanguage" @back-to-index="onBackToIndex" @back-to-map="onBackToMap" />
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import TopNavigation from '@/components/TopNavigation.vue'
import { withBase, getBasePath } from '@/utils/urlBase'

const TRACKER_SCRIPTS = [
  '/js/external/handtracker/WebARRocksHand.js',
  '/js/external/handtracker/three-r131.js',
  '/js/external/handtracker/GLTFLoader.js',
  '/js/external/handtracker/HandTrackerThreeHelper.js',
  '/js/external/handtracker/landmarksStabilizers/OneEuroLMStabilizer.js',
  '/js/external/handtracker/PoseFlipFilter.js',
]

const NN_PATH = '/neuralNets/'
const NN_WRIST_VERSION = '27'
const NN_WRISTBACK_VERSION = '24' // 官方註解：best: 24

// 手腕相關 VTO 模式（手掌朝鏡頭 wrist／手背朝鏡頭 wristBack）共用設定
const WRIST_MODES_COMMON_SETTINGS = {
  threshold: 0.95,
  poseLandmarksLabels: [
    'wristBack', 'wristLeft', 'wristRight', 'wristPalm',
    'wristPalmTop', 'wristBackTop', 'wristRightBottom', 'wristLeftBottom',
  ],
  isPoseFilter: true,
  occluderType: 'SOFTCYLINDER',
  occluderRadiusRange: [3.5, 4.5],
  occluderHeight: 48,
  occluderOffset: [0, 0, 0],
  occluderQuaternion: [0.707, 0, 0, 0.707], // 繞 X 軸轉 90 度
  occluderFlattenCoeff: 1.0,
  objectPointsPositionFactors: [1.0, 1.3, 1.0],
  landmarksStabilizerSpec: { minCutOff: 0.001, beta: 5 },
}

const VTO_MODES = {
  wrist: Object.assign(
    { NNsPaths: [NN_PATH + 'NN_WRIST_' + NN_WRIST_VERSION + '.json'] },
    WRIST_MODES_COMMON_SETTINGS
  ),
  wristBack: Object.assign(
    { NNsPaths: [NN_PATH + 'NN_WRISTBACK_' + NN_WRISTBACK_VERSION + '.json'] },
    WRIST_MODES_COMMON_SETTINGS,
    { isPoseFilter: true, threshold: 0.8 }
  ),
}

// 手環佔位模型：目前用官方 debug 用的手腕佔位 GLB，之後有正式無患子手環模型時直接換掉 url 即可
const WRIST_MODEL_COMMON_SETTINGS = {
  url: '/models/wristPlaceHolder2.glb',
  scale: 1.35 * 1.462,
  offset: [0.076, -0.916, -0.504],
  quaternion: [0, 0, 0, 1], // Format: X,Y,Z,W
}

const MODELS = {
  wristDemo: Object.assign({ VTOMode: 'wrist' }, WRIST_MODEL_COMMON_SETTINGS),
  wristBackDemo: Object.assign({ VTOMode: 'wristBack' }, WRIST_MODEL_COMMON_SETTINGS),
}

// 預設載入手背朝鏡頭模式（跟官方 demo 預設的 wristDemo 不同，這是這次要求的預設值）
const DEFAULT_MODEL = 'wristBackDemo'

// 平滑，跟內建濾波器完全獨立、互不影響。數值越小越平滑但延遲越高，可以在開發者模式現場調
const DEFAULT_SMOOTHING_ALPHA = 0.20

// 切換為 false 即關閉開發者模式切換按鈕
const DEV_MODE = true

export default {
  name: 'SoapberryBraceletPage',
  components: {
    TopNavigation,
  },
  data() {
    return {
      isReady: false,
      handDetected: false,
      errorMessage: '',
      indexLink: window.location.protocol + '//' + window.location.host + getBasePath(),
      // 手腕擺放教學圖，第一次進頁面預設顯示
      showInstructions: true,
      wristGuidelineSrc: withBase('/img/handtracker/wristGuideline2.png'),
      devMode: false,
      activeModel: '',
      devSmoothingAlpha: DEFAULT_SMOOTHING_ALPHA,
      languageTexts: {
        zh: {
          loadingText: '正在啟動相機與手部偵測…',
          wristHint: '請將後鏡頭對準你的手腕',
          errorCamera: '無法啟動相機／手部追蹤，請確認已允許相機權限',
          errorModel: '手部偵測模型載入失敗，請檢查網路連線後重新整理',
          instructionsText: '請將手掌垂直立起，讓整隻手完整入鏡',
          instructionsOk: '知道了',
        },
        en: {
          loadingText: 'Starting camera and hand detection…',
          wristHint: 'Point the back camera at your wrist',
          errorCamera: 'Could not start the camera / hand tracking. Please allow camera access.',
          errorModel: 'Failed to load the hand-tracking model. Check your connection and reload.',
          instructionsText: 'Display your hand vertically, fully visible',
          instructionsOk: 'Got it',
        },
        bn: {
          loadingText: 'カメラと手の検出を起動しています…',
          wristHint: '背面カメラを手首に向けてください',
          errorCamera: 'カメラ／手の追跡を起動できません。カメラの許可を確認してください',
          errorModel: '手の検出モデルの読み込みに失敗しました。接続を確認して再読み込みしてください',
          instructionsText: '手を縦にして、手全体がカメラに入るようにしてください',
          instructionsOk: 'わかりました',
        }
      }
    }
  },
  computed: {
    currentLanguage() {
      return Cookies.get('lang') || 'zh'
    },
  },
  async mounted() {
    if (DEV_MODE) {
      this.devMode = true
    }

    try {
      await this.loadTrackerScripts()
    } catch (error) {
      console.error('[Bracelet] WebAR.rocks.hand 腳本載入失敗:', error)
      this.errorMessage = this.languageTexts[this.currentLanguage].errorModel
      return
    }

    // 官方 demo 在呼叫 init() 之前會先手動設定 canvas 的實際像素寬高，
    this.sizeCanvas(this.$refs.handTrackerCanvas)
    this.sizeCanvas(this.$refs.vtoCanvas)

    this._vtoMode = MODELS[DEFAULT_MODEL].VTOMode
    const vtoModeSettings = VTO_MODES[this._vtoMode]

    try {
      const three = await window.HandTrackerThreeHelper.init({
        handTrackerCanvas: this.$refs.handTrackerCanvas,
        VTOCanvas: this.$refs.vtoCanvas,
        // 維持後鏡頭（拍自己的手腕，不是自拍鏡頭）
        videoSettings: { facingMode: 'environment' },
        maxHandsDetected: 1,
        landmarksStabilizerSpec: vtoModeSettings.landmarksStabilizerSpec,
        objectPointsPositionFactors: vtoModeSettings.objectPointsPositionFactors,
        poseLandmarksLabels: vtoModeSettings.poseLandmarksLabels,
        poseFilter: vtoModeSettings.isPoseFilter ? window.PoseFlipFilter.instance({}) : null,
        NNsPaths: vtoModeSettings.NNsPaths.map(withBase),
        threshold: vtoModeSettings.threshold,
        callbackTrack: (detectState) => {
          const isDetected = !!(detectState && detectState.isDetected)
          this.handDetected = isDetected
          // 這個 callback 會在 WebAR.rocks.hand 算完這一幀的姿態矩陣之後、渲染之前執行，
          if (isDetected) {
            this.smoothTrackerPose()
          }
          this._wasDetected = isDetected
        },
      })
      this._initialized = true
      this._state = 'idle'

      this.handleResize()
      this._onResize = () => this.handleResize()
      window.addEventListener('resize', this._onResize)

      this.start(three)
    } catch (error) {
      console.error('[Bracelet] 手部追蹤初始化失敗:', error)
      const detail = (error && error.message) ? error.message : String(error)
      this.errorMessage = this.languageTexts[this.currentLanguage].errorCamera + '\n(' + detail + ')'
    }
  },
  beforeUnmount() {
    this.cleanup()
  },
  methods: {
    loadScript(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = withBase(src)
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Failed to load script: ' + src))
        document.head.appendChild(script)
      })
    },
    // classic script 之間靠全域變數溝通，載入順序要固定，所以逐一 await，不能平行載入
    async loadTrackerScripts() {
      if (window.HandTrackerThreeHelper) return // 已經載入過（例如上一次進到這頁沒離開）
      for (const src of TRACKER_SCRIPTS) {
        await this.loadScript(src)
      }
      this.exposeTrackerGlobals()
    },
    // HandTrackerThreeHelper.js 跟 PoseFlipFilter.js 裡面是用 `const X = (function(){...})()`
    // 讓我們 Vue 元件（webpack 打包出來的程式碼，跟這些變數不在同一個宣告作用域）也能存取到
    exposeTrackerGlobals() {
      const script = document.createElement('script')
      script.textContent = 'window.HandTrackerThreeHelper = HandTrackerThreeHelper; window.PoseFlipFilter = PoseFlipFilter;'
      document.head.appendChild(script)
    },

    // 對照官方 demo 的 setup_lighting()
    setupLighting(three) {
      const THREE = window.THREE
      const hemiLight = new THREE.HemisphereLight(0xffffff, 0x000000, 2)
      three.scene.add(hemiLight)
      const pointLight = new THREE.PointLight(0xffffff, 2)
      pointLight.position.set(0, 100, 0)
      three.scene.add(pointLight)
    },

    // 對照官方 demo 的 start()：先加燈光，loadingManager 全部載完才關掉載入畫面，
    // 接著上遮擋圓柱體、載入預設模型
    start(three) {
      this._three = three
      // 存起來給 smoothTrackerPose() 直接讀寫姿態矩陣用
      this._trackerParent = three.trackersParent[0]
      this.setupLighting(three)

      three.loadingManager.onLoad = () => {
        this.isReady = true
      }

      this.setOccluder().then(() => {
        this._state = 'idle'
      }).then(() => {
        this.loadModel(DEFAULT_MODEL)
      })
    },

    // 對照官方 demo 的 set_occluder()（我們只用得到 wrist/wristBack 的 SOFTCYLINDER 那條分支）
    setOccluder() {
      const vtoModeSettings = VTO_MODES[this._vtoMode]
      if (vtoModeSettings.occluderType === 'SOFTCYLINDER') {
        return this.addSoftOccluder(vtoModeSettings)
      }
      return Promise.resolve()
    },

    // 對照官方 demo 的 add_softOccluder()：用一個只寫深度、不上色的圓柱體蓋在手腕位置，
    // 讓手環在被手腕擋住的角度自然消失，而不是穿透手腕浮著
    addSoftOccluder(vtoModeSettings) {
      const THREE = window.THREE
      const occluderRadius = vtoModeSettings.occluderRadiusRange[1]
      const occluderMesh = new THREE.Mesh(
        new THREE.CylinderGeometry(occluderRadius, occluderRadius, vtoModeSettings.occluderHeight, 32, 1, true),
        new THREE.MeshNormalMaterial()
      )
      const dr = vtoModeSettings.occluderRadiusRange[1] - vtoModeSettings.occluderRadiusRange[0]
      occluderMesh.position.fromArray(vtoModeSettings.occluderOffset)
      occluderMesh.quaternion.fromArray(vtoModeSettings.occluderQuaternion)
      occluderMesh.scale.set(1.0, 1.0, vtoModeSettings.occluderFlattenCoeff)
      window.HandTrackerThreeHelper.add_threeSoftOccluder(occluderMesh, occluderRadius, dr, false)
      return Promise.resolve()
    },

    // 在 WebAR.rocks.hand 算完這一幀姿態、渲染之前，自己再疊加一層跟內建濾波器完全獨立的
    // EMA 平滑：位置用線性內插（lerp），角度用球面線性內插（slerp）。devSmoothingAlpha
    // 越小越平滑但延遲越高，可以在開發者模式的面板即時調整
    smoothTrackerPose() {
      if (!this._trackerParent) return
      const THREE = window.THREE
      const pos = new THREE.Vector3()
      const quat = new THREE.Quaternion()
      const scale = new THREE.Vector3()
      this._trackerParent.matrix.decompose(pos, quat, scale)

      if (!this._wasDetected) {
        // 剛重新偵測到手腕：直接採用這一幀的姿態，不要跟舊姿態內插，
        // 不然手腕重新入鏡的瞬間會從上一次消失的位置飄過來
        this._smoothedPosition = pos.clone()
        this._smoothedQuaternion = quat.clone()
      } else {
        this._smoothedPosition.lerp(pos, this.devSmoothingAlpha)
        this._smoothedQuaternion.slerp(quat, this.devSmoothingAlpha)
      }

      this._trackerParent.matrix.compose(this._smoothedPosition, this._smoothedQuaternion, scale)
    },

    // 對照官方 demo 的 change_VTOMode()：切換 wrist／wristBack 時要連帶換掉神經網路、
    // landmark 標籤、pose filter，再重新套用遮擋體
    changeVTOMode(newVTOMode) {
      window.HandTrackerThreeHelper.clear_threeObjects(true)
      const vtoModeSettings = VTO_MODES[newVTOMode]
      return window.HandTrackerThreeHelper.update({
        landmarksStabilizerSpec: vtoModeSettings.landmarksStabilizerSpec,
        objectPointsPositionFactors: vtoModeSettings.objectPointsPositionFactors,
        poseLandmarksLabels: vtoModeSettings.poseLandmarksLabels,
        poseFilter: vtoModeSettings.isPoseFilter ? window.PoseFlipFilter.instance({}) : null,
        NNsPaths: vtoModeSettings.NNsPaths.map(withBase),
        threshold: vtoModeSettings.threshold,
      }).then(() => {
        this._vtoMode = newVTOMode
        return this.setOccluder()
      }).then(() => {
        this._state = 'idle'
      })
    },

    // 對照官方 demo 的 load_model()：如果目標模型需要不同的 VTOMode，先切換模式再重新呼叫自己
    loadModel(modelId) {
      if ((this._state !== 'running' && this._state !== 'idle') || modelId === this._vtoModel) {
        return // 模型已經是這個、或目前正在忙／還沒準備好
      }
      this._state = 'busy'
      const modelSettings = MODELS[modelId]

      window.HandTrackerThreeHelper.clear_threeObjects(false)

      if (modelSettings.VTOMode !== this._vtoMode) {
        this.changeVTOMode(modelSettings.VTOMode).then(() => {
          this.loadModel(modelId)
        })
        return
      }

      const THREE = window.THREE
      new THREE.GLTFLoader(this._three.loadingManager).load(withBase(modelSettings.url), (model) => {
        const mesh = model.scene.children[0]
        mesh.scale.set(1, 1, 1)

        if (modelSettings.scale) {
          mesh.scale.multiplyScalar(modelSettings.scale)
        }
        if (modelSettings.offset) {
          const d = modelSettings.offset
          const displacement = new THREE.Vector3(d[0], d[2], -d[1]) // inverse Y and Z
          mesh.position.add(displacement)
        }
        if (modelSettings.quaternion) {
          const q = modelSettings.quaternion
          mesh.quaternion.set(q[0], q[2], -q[1], q[3])
        }

        window.HandTrackerThreeHelper.add_threeObject(mesh)
        this._vtoModel = modelId
        this.activeModel = modelId
        this._state = 'running'
      })
    },

    // devSmoothingAlpha 每一幀都會被 smoothTrackerPose() 讀取，改完立刻生效
    devAdjustSmoothing(delta) {
      this.devSmoothingAlpha = Math.max(0.05, Math.min(1, parseFloat((this.devSmoothingAlpha + delta).toFixed(2))))
    },

    // 沿用官方 demo 的 size_canvas() 公式：寬度取 min(innerWidth, innerHeight)，
    // 直向手機時等於全螢幕寬，橫向時則裁成正方形視窗 — 這是偵測用輸入視窗的慣例尺寸，不是我們自己拍的
    computeCanvasSize() {
      const dpr = window.devicePixelRatio || 1
      return {
        w: Math.round(dpr * Math.min(window.innerWidth, window.innerHeight)),
        h: Math.round(dpr * window.innerHeight),
      }
    },
    sizeCanvas(cv) {
      const { w, h } = this.computeCanvasSize()
      cv.width = w
      cv.height = h
    },
    handleResize() {
      if (!window.HandTrackerThreeHelper || !this._initialized) return
      const { w, h } = this.computeCanvasSize()
      window.HandTrackerThreeHelper.resize(w, h)
    },

    cleanup() {
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize)
        this._onResize = null
      }
      if (this._initialized && window.HandTrackerThreeHelper) {
        try { window.HandTrackerThreeHelper.destroy() } catch (e) { /* ignore */ }
        this._initialized = false
      }
    },

    onBackToIndex() {
      this.cleanup()
      window.location.href = this.indexLink
    },
    onBackToMap() {
      this.cleanup()
      window.location.href = './mapPage'
    },
  }
}
</script>

<style scoped>
#main_page {
  height: 100svh;
  overflow: hidden;
  position: relative;
  background: #000;
}

.tracker-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.vto-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  pointer-events: none;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 500;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
}

.loading-spinner {
  width: 44px;
  height: 44px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-overlay p {
  margin-top: 16px;
  font-size: 14px;
}

.error-overlay {
  position: absolute;
  inset: 0;
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  text-align: center;
  font-size: 15px;
  white-space: pre-wrap;
}

.instructions-overlay {
  position: absolute;
  inset: 0;
  z-index: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.75);
}

.instructions-box {
  background: #222;
  color: #fff;
  border-radius: 16px;
  padding: 20px;
  max-width: 320px;
  width: 100%;
  text-align: center;
}

.instructions-text {
  font-size: 15px;
  line-height: 1.6;
  color: #fff;
  margin: 0 0 12px;
}

.instructions-img {
  width: 100%;
  border-radius: 10px;
  margin-bottom: 16px;
}

.instructions-ok-btn {
  background: linear-gradient(180deg, #D9A441 0%, #B4790F 100%);
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 10px 32px;
  font-size: 14px;
  cursor: pointer;
}

.instructions-reopen {
  position: fixed;
  bottom: 12%;
  right: 16px;
  z-index: 400;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  user-select: none;
}

.wrist-hint {
  position: absolute;
  bottom: 12%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 400;
  pointer-events: none;
  text-align: center;
}

.wrist-hint-text {
  display: inline-block;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  padding: 10px 18px;
  border-radius: 999px;
  font-size: 14px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
}

/* ── 開發者模式：模式切換按鈕（對照官方 demo 的 .controls） ── */
.dev-controls {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 10000;
  display: flex;
}

.dev-mode-btn {
  flex: 1;
  background: rgba(0, 0, 0, 0.55);
  color: #eee;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  padding: 12px 6px;
  font-size: 13px;
  cursor: pointer;
}

.dev-mode-btn.active {
  background: rgba(67, 106, 178, 0.75);
  color: #fff;
  font-weight: bold;
}

.dev-smoothing-value {
  flex: 0 0 44px;
  cursor: default;
  font-variant-numeric: tabular-nums;
}
</style>
